import React, { useState, useEffect, useRef } from 'react';
import styles from './ExpandableVideo.module.css';
import { VideoContent } from '../../services/teachingAgent';
import { teachingAgentAPI } from '../../services/teachingAgent';

interface ExpandableVideoProps {
  video: VideoContent;
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onFullscreen: () => void;
  onDismiss: () => void;
}

const ExpandableVideo: React.FC<ExpandableVideoProps> = ({
  video,
  expanded,
  onExpand,
  onCollapse,
  onFullscreen,
  onDismiss,
}) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Fetch signed video URL
  useEffect(() => {
    const fetchUrl = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const url = await teachingAgentAPI.getVideoUrl(video.videoId, video.startTime);
        setVideoUrl(url);
      } catch (err) {
        setError('Failed to load video');
        console.error('Video load error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUrl();
  }, [video.videoId, video.startTime]);

  const handleToggle = () => {
    if (expanded) {
      onCollapse();
    } else {
      onExpand();
    }
  };

  return (
    <div className={`${styles.container} ${expanded ? styles.expanded : styles.collapsed}`}>
      {/* Header */}
      <div className={styles.header} onClick={handleToggle}>
        <div className={styles.headerContent}>
          <span className={styles.videoIcon}>🎬</span>
          <div className={styles.videoInfo}>
            <span className={styles.videoTitle}>{video.title}</span>
            <span className={styles.videoReason}>{video.reason}</span>
          </div>
        </div>
        <div className={styles.headerActions}>
          {expanded && (
            <>
              <button
                className={styles.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onFullscreen();
                }}
                title="Fullscreen"
              >
                ⛶
              </button>
            </>
          )}
          <button
            className={styles.actionButton}
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            title="Dismiss"
          >
            ✕
          </button>
          <span className={styles.toggleIcon}>
            {expanded ? '▼' : '▲'}
          </span>
        </div>
      </div>

      {/* Video Player */}
      {expanded && (
        <div className={styles.playerContainer}>
          {isLoading && (
            <div className={styles.loading}>
              <span className={styles.spinner}>⏳</span>
              Loading video...
            </div>
          )}

          {error && (
            <div className={styles.error}>
              <span className={styles.errorIcon}>⚠️</span>
              {error}
            </div>
          )}

          {videoUrl && !isLoading && !error && (
            <video
              ref={videoRef}
              className={styles.video}
              src={videoUrl}
              controls
              autoPlay={false}
              playsInline
            >
              Your browser does not support video playback.
            </video>
          )}

          {/* Fallback for HLS streams */}
          {videoUrl && videoUrl.includes('.m3u8') && (
            <div className={styles.hlsNote}>
              <p>For HLS streaming, use a compatible player.</p>
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.externalLink}
              >
                Open in external player ↗
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpandableVideo;
